import React, { useState, useEffect, useMemo } from 'react';
import { Card, Table, Button, Select, Space, Tag, Divider, message, Spin, Switch } from 'antd';
import { DownloadOutlined, AimOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import api from '../utils/api';
import { getApiBaseURL } from '../utils/api';
import { getToken } from '../utils/auth';
import { getDataForms, PHD_TYPES, getSubYear } from '../utils/dataForms';

// ─── Helper functions (consistent with PhdExportMatrixPreview) ─────

function getCellValueFromSubmission(sub, fieldKey) {
  if (sub.cells && sub.cells.length > 0) {
    const cell = [...sub.cells].reverse().find(c => c.field_key === fieldKey);
    if (cell && cell.value !== null && cell.value !== undefined && cell.value !== '') {
      const n = Number(cell.value);
      return isNaN(n) ? cell.value : n;
    }
    return null;
  }
  return null;
}

function latestByYear(submissions, dataType) {
  const typeSubs = (submissions || []).filter(s => s.data_type === dataType && s.status === 'approved');
  const sorted = [...typeSubs].sort((a, b) => {
    const ta = new Date(a.reviewed_at || a.submitted_at || 0).getTime();
    const tb = new Date(b.reviewed_at || b.submitted_at || 0).getTime();
    return ta - tb;
  });
  const byYear = new Map();
  sorted.forEach(s => {
    const y = getSubYear(s);
    if (y == null) return;
    const cur = byYear.get(y);
    if (!cur) {
      byYear.set(y, { ...s, cells: [...(s.cells || [])] });
      return;
    }
    const mergedCells = [...(cur.cells || [])];
    (s.cells || []).forEach(c => {
      const idx = mergedCells.findIndex(mc => mc.field_key === c.field_key);
      if (idx >= 0) mergedCells[idx] = c;
      else mergedCells.push(c);
    });
    byYear.set(y, { ...cur, cells: mergedCells });
  });
  return byYear;
}

function findTarget(targetRecords, dataType, fieldKey, year) {
  if (!targetRecords?.length) return null;
  const range = targetRecords.find(
    t => t.data_type === dataType && t.field_key === fieldKey
      && t.start_year && t.end_year && year >= t.start_year && year <= t.end_year
  );
  if (range) return range.target_value;
  const exact = targetRecords.find(
    t => t.data_type === dataType && t.field_key === fieldKey && t.target_year === year
  );
  if (exact) return exact.target_value;
  const total = targetRecords.find(
    t => t.data_type === dataType && t.field_key === fieldKey
      && !t.start_year && !t.end_year
  );
  if (total) return total.target_value;
  return null;
}

function findTotalTarget(targetRecords, dataType, fieldKey) {
  if (!targetRecords?.length) return null;
  const total = targetRecords.find(
    t => t.data_type === dataType && t.field_key === fieldKey
      && (!t.start_year || t.start_year === null)
      && (!t.end_year || t.end_year === null)
  );
  return total ? total.target_value : null;
}

function calcRate(value, target) {
  if (target == null || target === 0) return null;
  if (value == null) return 0;
  return (value / target) * 100;
}

function getRateDisplay(rate) {
  if (rate == null) return { text: '—', color: '#999' };
  const fmt = `${rate.toFixed(1)}%`;
  if (rate < 100) return { text: fmt, color: '#faad14' };
  if (rate === 100) return { text: fmt, color: '#52c41a' };
  return { text: fmt, color: '#1677ff' };
}

// ─── PhD overview component (core indicators — single summary table) ──

const PhdOverview = ({ departments, user }) => {
  const [allSubmissions, setAllSubmissions] = useState([]);
  const [targetRecords, setTargetRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDeptId, setSelectedDeptId] = useState(null);
  const [yearStart, setYearStart] = useState(null);
  const [yearEnd, setYearEnd] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [showTargets, setShowTargets] = useState(true);

  // ── Data loading ──

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const allSubs = [];
        for (const dept of departments || []) {
          try {
            const resp = await api.get('/submissions', {
              params: { department_id: dept.id },
            });
            const filtered = (resp.data || []).filter(sub => {
              if (!sub.data_content) return true;
              const content = typeof sub.data_content === 'string'
                ? JSON.parse(sub.data_content)
                : sub.data_content;
              return !content || !content._replaced_by;
            });
            allSubs.push(...filtered.map(s => ({ ...s, _dept_id: dept.id, _dept_name: dept.name })));
          } catch { /* skip */ }
        }
        setAllSubmissions(allSubs);

        const tgtResp = await api.get('/indicator-targets');
        setTargetRecords(tgtResp.data || []);
      } catch (e) {
        console.error('加载数据失败:', e);
        message.error('加载 PhD 数据失败');
      } finally {
        setLoading(false);
      }
    };
    if (departments?.length) {
      loadData();
    }
  }, [departments]);

  // ── Find departments with PhD data ──

  const phdDepartments = useMemo(() => {
    const deptIdsWithPhD = new Set();
    allSubmissions.forEach(sub => {
      if (PHD_TYPES.includes(sub.data_type)) {
        deptIdsWithPhD.add(sub._dept_id);
      }
    });
    return (departments || []).filter(d => deptIdsWithPhD.has(d.id));
  }, [allSubmissions, departments]);

  useEffect(() => {
    if (!selectedDeptId && phdDepartments.length > 0) {
      setSelectedDeptId(phdDepartments[0].id);
    }
  }, [phdDepartments, selectedDeptId]);

  // ── Current department data ──

  const currentSubmissions = useMemo(() => {
    if (!selectedDeptId) return [];
    return allSubmissions.filter(s => s._dept_id === selectedDeptId);
  }, [allSubmissions, selectedDeptId]);

  const currentDept = useMemo(() => {
    return (departments || []).find(d => d.id === selectedDeptId);
  }, [departments, selectedDeptId]);

  const dataForms = useMemo(() => {
    if (!currentDept) return [];
    return getDataForms(currentDept.name || '');
  }, [currentDept]);

  const phdForms = useMemo(() => {
    return dataForms.filter(f => PHD_TYPES.includes(f.key));
  }, [dataForms]);

  // ── Merged data by type and year ──

  const typeYearMap = useMemo(() => {
    const map = {};
    PHD_TYPES.forEach(dt => {
      map[dt] = latestByYear(currentSubmissions, dt);
    });
    return map;
  }, [currentSubmissions]);

  // Union of all years
  const allYears = useMemo(() => {
    const set = new Set();
    currentSubmissions.forEach(sub => {
      const y = getSubYear(sub);
      if (y != null) set.add(y);
    });
    return [...set].sort((a, b) => a - b);
  }, [currentSubmissions]);

  // Default year range covers all years with data
  useEffect(() => {
    if (allYears.length > 0) {
      setYearStart(Math.min(...allYears));
      setYearEnd(Math.max(...allYears));
    }
  }, [allYears]);

  const yearOptions = allYears;
  const displayYears = [];
  if (yearStart != null && yearEnd != null) {
    for (let y = yearStart; y <= yearEnd; y++) displayYears.push(y);
  }

  const hasAnyData = displayYears.length > 0 && PHD_TYPES.some(dt => typeYearMap[dt]?.size > 0);

  // ── Merge rows for all data types ──

  const rows = useMemo(() => {
    const result = [];
    phdForms.forEach(form => {
      const fields = (form.fields || []).filter(f => f.key !== '年份' && f.key !== 'year');
      fields.forEach((field, idx) => {
        result.push({
          key: `${form.key}::${field.key}`,
          dataType: form.key,
          indicatorLabel: form.label,
          indicatorRowSpan: 0,
          fieldKey: field.key,
          taskLabel: field.label,
        });
      });
    });
    // Merge rows with same indicator using rowSpan
    for (let i = 0; i < result.length;) {
      const label = result[i].indicatorLabel;
      let j = i + 1;
      while (j < result.length && result[j].indicatorLabel === label) j++;
      result[i].indicatorRowSpan = j - i;
      for (let k = i + 1; k < j; k++) result[k].indicatorRowSpan = 0;
      i = j;
    }
    return result;
  }, [phdForms]);

  // ── Build columns ──

  const columns = useMemo(() => {
    const cols = [
      {
        title: '指标类型',
        key: 'indicator',
        width: 110,
        fixed: 'left',
        render: (_, record) => {
          if (record.indicatorRowSpan === 0) {
            return { children: null, props: { rowSpan: 0 } };
          }
          return {
            children: <span style={{ fontWeight: 600, fontSize: 13 }}>{record.indicatorLabel}</span>,
            props: { rowSpan: record.indicatorRowSpan || 1 },
          };
        },
      },
      {
        title: '任务项',
        dataIndex: 'taskLabel',
        key: 'task',
        width: 260,
        fixed: 'left',
        render: (text) => (
          <span style={{ paddingLeft: text.startsWith('其中:') ? 20 : 0, fontSize: 13 }}>
            {text}
          </span>
        ),
      },
    ];

    if (showTargets) {
      // Expanded mode: year columns show data_value/target/achievement_rate
      displayYears.forEach(y => {
        cols.push({
          title: `${y}年`,
          key: `y-${y}`,
          width: 270,
          align: 'center',
          children: [
            {
              title: '数据值',
              key: `y-${y}-val`,
              width: 90,
              align: 'center',
              render: (_, row) => {
                const typeYears = typeYearMap[row.dataType];
                const sub = typeYears?.get(y);
                if (!sub) return '—';
                const v = getCellValueFromSubmission(sub, row.fieldKey);
                return v != null ? <span style={{ fontWeight: 600, fontSize: 15 }}>{v}</span> : '—';
              },
            },
            {
              title: '目标值',
              key: `y-${y}-tgt`,
              width: 90,
              align: 'center',
              render: (_, row) => {
                const t = findTarget(targetRecords, row.dataType, row.fieldKey, y);
                return t != null ? <span style={{ color: '#1677ff' }}>{t}</span> : '—';
              },
            },
            {
              title: '达成率',
              key: `y-${y}-rate`,
              width: 90,
              align: 'center',
              render: (_, row) => {
                const typeYears = typeYearMap[row.dataType];
                const sub = typeYears?.get(y);
                const val = sub ? getCellValueFromSubmission(sub, row.fieldKey) : null;
                const tgt = findTarget(targetRecords, row.dataType, row.fieldKey, y);
                const rate = calcRate(val, tgt);
                const { text, color } = getRateDisplay(rate);
                return <span style={{ color, fontWeight: 600 }}>{text}</span>;
              },
            },
          ],
        });
      });

      // Total target column (expanded to data_value/target/achievement_rate)
      cols.push({
        title: '总目标',
        key: 'total_target_group',
        align: 'center',
        children: [
          {
            title: '数据值',
            key: 'total_target_val',
            width: 90,
            align: 'center',
            render: (_, row) => {
              const typeYears = typeYearMap[row.dataType];
              if (!typeYears) return '—';
              let latestVal = null;
              let latestY = -Infinity;
              for (const [y, sub] of typeYears.entries()) {
                if (!sub) continue;
                const v = getCellValueFromSubmission(sub, row.fieldKey);
                if (v != null && y > latestY) { latestVal = v; latestY = y; }
              }
              return latestVal != null ? <span style={{ fontWeight: 600, fontSize: 15 }}>{latestVal}</span> : '—';
            },
          },
          {
            title: '目标值',
            key: 'total_target_tgt',
            width: 90,
            align: 'center',
            render: (_, row) => {
              const t = findTotalTarget(targetRecords, row.dataType, row.fieldKey);
              return t != null
                ? <span style={{ color: '#722ed1', fontWeight: 600 }}>{t}</span>
                : '—';
            },
          },
          {
            title: '达成率',
            key: 'total_target_rate',
            width: 90,
            align: 'center',
            render: (_, row) => {
              const typeYears = typeYearMap[row.dataType];
              if (!typeYears) return '—';
              let latestVal = null;
              let latestY = -Infinity;
              for (const [y, sub] of typeYears.entries()) {
                if (!sub) continue;
                const v = getCellValueFromSubmission(sub, row.fieldKey);
                if (v != null && y > latestY) { latestVal = v; latestY = y; }
              }
              const tgt = findTotalTarget(targetRecords, row.dataType, row.fieldKey);
              const rate = calcRate(latestVal, tgt);
              const { text, color } = getRateDisplay(rate);
              return <span style={{ color, fontWeight: 600 }}>{text}</span>;
            },
          },
        ],
      });
    } else {
      // Simple mode: year columns show only data_value, no total target column
      displayYears.forEach(y => {
        cols.push({
          title: `${y}年`,
          key: `y-${y}`,
          width: 100,
          align: 'center',
          render: (_, row) => {
            const typeYears = typeYearMap[row.dataType];
            const sub = typeYears?.get(y);
            if (!sub) return '—';
            const v = getCellValueFromSubmission(sub, row.fieldKey);
            return v != null ? <span style={{ fontWeight: 600, fontSize: 15 }}>{v}</span> : '—';
          },
        });
      });
    }

    return cols;
  }, [displayYears, typeYearMap, targetRecords, showTargets]);

  // ── Export summary table ──

  const handleExportTotal = async () => {
    if (!selectedDeptId) { message.warning('请先选择部门'); return; }
    setExporting(true);
    try {
      const token = getToken();
      if (!token) { message.error('未登录'); return; }
      const apiUrl = getApiBaseURL();
      const params = new URLSearchParams({
        department_id: selectedDeptId,
        include_targets: showTargets ? 'true' : 'false',
        year_start: yearStart,
        year_end: yearEnd,
      });
      const resp = await fetch(
        `${apiUrl}/submissions/export-phd-total?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!resp.ok) throw new Error('下载失败');
      const blob = await resp.blob();
      const disposition = resp.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename\*?=(?:UTF-8'')?([^;\s]+)/);
      const filename = match
        ? decodeURIComponent(match[1])
        : `${currentDept?.name || '博士授权点'}_总表_${yearStart}-${yearEnd}.xlsx`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (e) {
      message.error(e.message || '下载失败');
    } finally {
      setExporting(false);
    }
  };

  // ── Render ──

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Spin size="large" />
        <p style={{ marginTop: 16, color: '#999' }}>加载博士授权点数据...</p>
      </div>
    );
  }

  if (!phdDepartments.length) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: 60, color: '#999' }}>
          <p style={{ fontSize: 16 }}>暂无博士授权点数据</p>
        </div>
      </Card>
    );
  }

  return (
    <div>
      {/* Top: department selection + year range + export */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <span style={{ fontWeight: 600 }}>选择部门：</span>
          <Select
            value={selectedDeptId}
            onChange={setSelectedDeptId}
            style={{ width: 220 }}
            options={phdDepartments.map(d => ({ label: d.name, value: d.id }))}
          />
          <Divider type="vertical" />
          <span style={{ color: '#666' }}>年份范围：</span>
          <Select
            value={yearStart}
            onChange={setYearStart}
            style={{ width: 90 }}
            size="small"
            options={yearOptions.map(y => ({ label: String(y), value: y }))}
          />
          <span style={{ color: '#666' }}>—</span>
          <Select
            value={yearEnd}
            onChange={setYearEnd}
            style={{ width: 90 }}
            size="small"
            options={yearOptions.map(y => ({ label: String(y), value: y }))}
          />
          <Divider type="vertical" />
          <Tag color="blue">{phdDepartments.length} 个部门有数据</Tag>
          <Tag color="green">{targetRecords.length} 条目标</Tag>
          <Divider type="vertical" />
          <span style={{ color: '#666' }}>显示目标</span>
          <Switch
            checked={showTargets}
            onChange={setShowTargets}
            checkedChildren={<EyeOutlined />}
            unCheckedChildren={<EyeInvisibleOutlined />}
          />
          <Divider type="vertical" />
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleExportTotal}
            loading={exporting}
            disabled={!hasAnyData}
          >
            导出总表
          </Button>
        </Space>
      </Card>

      {/* Unified summary table */}
      {!hasAnyData || !phdForms.length ? (
        <Card>
          <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
            当前部门在所选年份范围内暂无博士授权点数据
          </div>
        </Card>
      ) : (
        <Card
          size="small"
          title={
            <Space>
              <AimOutlined style={{ color: '#1677ff' }} />
              <span style={{ fontWeight: 600 }}>
                {currentDept?.name || ''} 博士授权点数据总览
              </span>
              <Tag color="blue">{rows.length} 个数据项</Tag>
            </Space>
          }
        >
          <div style={{ marginBottom: 12, color: '#888', fontSize: 13 }}>
            已展开显示数据值、目标值和达成率，拖拽横向滚动查看全部年份。
          </div>
          <Table
            columns={columns}
            dataSource={rows}
            rowKey="key"
            pagination={false}
            size="small"
            bordered
            scroll={{ x: Math.max(800, 370 + displayYears.length * 270 + 270) }}
          />
        </Card>
      )}
    </div>
  );
};

export default PhdOverview;
